// Wave 20.47g — Aero positions panel.
//
// Big illustrated cards (map-sized) instead of a compact list.
// Each card has the pose glyph as centerpiece + rider-authored
// alias + best/mean CdA + sparkline + rename affordance. Tapping
// the card navigates to /positions/:class for a fuller drill-in.

import React from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { colors } from '../theme';
import { positionColor } from '../services/positionColors';
import { usePositionsAnalytics, type PositionStat } from '../hooks/usePositionsAnalytics';
import { seedCorePositions, type SeededPosition } from '../services/positionDefaults';
import { labelForPosition } from '../vision/positionClassifier';
import PoseGlyph from './PoseGlyph';

function formatHoldMinutes(sec: number): string {
  const m = Math.round(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

interface Props {
  height?: number | string;
}

export default function AeroPositionsPanel({ height = '100%' }: Props) {
  const { data, loading, error, errorMessage } = usePositionsAnalytics();

  // Wave 20.49ck — Always show the 4 core positions ranked worst
  // → best CdA (Standing highest → Drops lowest). Tested rows show
  // measured CdA; untested rows are seeded from the rider's most-
  // characterised position via CD ratios and rendered grayed out.
  const seeded: SeededPosition[] = data ? seedCorePositions(data.positions) : [];
  // Any non-core classes the rider HAS tested (e.g. aero_tuck, tt)
  // still appear underneath the 4 core rows so nothing gets hidden.
  const extras = data
    ? data.positions.filter((p) => !['standing', 'tops', 'hoods', 'drops'].includes(p.class))
        .sort((a, b) => a.bestCda - b.bestCda)
    : [];

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.surface,
        overflow: 'hidden',
        height,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 320,
      }}
    >
      <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Typography variant="overline" sx={{ color: colors.brand, letterSpacing: '0.2em' }}>
          Aero positions
        </Typography>
        <Typography sx={{ fontWeight: 900, fontSize: '1.05rem', lineHeight: 1.1 }}>
          Your CdAs
        </Typography>
        {data && data.totalSessions > 0 && (
          <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem', mt: 0.25 }}>
            {data.totalSessions} session{data.totalSessions === 1 ? '' : 's'} ·{' '}
            {data.lifetimeBest.cda != null && data.lifetimeBest.class
              ? `Lifetime best ${data.lifetimeBest.cda.toFixed(3)} (${data.lifetimeBest.class})`
              : 'No lifetime best yet'}
          </Typography>
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 1.5 }}>
        {loading && (
          <Stack alignItems="center" sx={{ py: 5, color: colors.textMuted }}>
            <CircularProgress size={22} sx={{ color: colors.brand }} />
          </Stack>
        )}

        {!loading && error && (
          <Stack alignItems="center" sx={{ py: 4, px: 2, textAlign: 'center', color: colors.textMuted }} spacing={1.25}>
            <Typography sx={{ fontSize: '0.85rem' }}>{errorMessage}</Typography>
            {error === 'paywall' && (
              <Button
                component={RouterLink}
                to="/session"
                size="small"
                variant="outlined"
                sx={{ borderColor: colors.brand, color: colors.brand, fontWeight: 700 }}
              >
                Start a session
              </Button>
            )}
          </Stack>
        )}

        {!loading && !error && data != null && (
          <Stack spacing={1.25}>
            {seeded.map((s, idx) => (
              <SeededPositionCard key={s.class} s={s} rank={idx + 1} />
            ))}
            {extras.map((p, idx) => (
              <PositionCard key={p.class} p={p} rank={seeded.length + idx + 1} />
            ))}
            {seeded.every((s) => !s.tested) && (
              <Typography
                sx={{
                  color: colors.textMuted,
                  fontSize: '0.75rem',
                  textAlign: 'center',
                  fontStyle: 'italic',
                  mt: 0.5,
                }}
              >
                All four rows are defaults. Run an aero session to
                measure your real numbers.
              </Typography>
            )}
          </Stack>
        )}
      </Box>

      {!loading && !error && data != null && (
        <Box sx={{ borderTop: `1px solid ${colors.border}`, px: 1.5, py: 0.75, textAlign: 'center' }}>
          <Button
            component={RouterLink}
            to="/analytics"
            size="small"
            sx={{ color: colors.brand, fontWeight: 700, fontSize: '0.7rem' }}
          >
            Full analytics →
          </Button>
        </Box>
      )}
    </Box>
  );
}

function SeededPositionCard({ s, rank }: { s: SeededPosition; rank: number }) {
  // Tested rows behave exactly like the original PositionCard.
  if (s.tested && s.stat) {
    return <PositionCard p={s.stat} rank={rank} />;
  }
  // Default rows: grayed pose, "default" badge, tag showing what
  // the seed was derived from (rider's anchor position, or nominal
  // frontal-area fallback). No RouterLink — nothing to drill into.
  const c = positionColor(s.class);
  return (
    <Box
      sx={{
        display: 'block',
        borderRadius: 3,
        border: `1px dashed ${colors.border}`,
        backgroundColor: colors.surfaceElevated,
        overflow: 'hidden',
        opacity: 0.55,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          backgroundColor: 'rgba(15,15,20,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 1,
          filter: 'grayscale(0.6)',
        }}
      >
        <PoseGlyph positionClass={s.class} size={200} />
        <Typography
          sx={{
            position: 'absolute',
            top: 8,
            left: 10,
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.7rem',
            color: colors.textFaint,
            fontWeight: 700,
          }}
        >
          #{rank}
        </Typography>
        <Typography
          sx={{
            position: 'absolute',
            top: 8,
            right: 10,
            fontSize: '0.6rem',
            color: colors.textMuted,
            letterSpacing: '0.2em',
            fontWeight: 800,
            border: `1px solid ${colors.border}`,
            px: 0.75,
            py: 0.15,
            borderRadius: 1,
          }}
        >
          DEFAULT
        </Typography>
      </Box>
      <Box sx={{ px: 1.5, py: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                color: c,
                fontWeight: 800,
                fontSize: '0.9rem',
                letterSpacing: '0.02em',
              }}
            >
              {labelForPosition(s.class as any).toUpperCase()}
            </Typography>
            <Typography sx={{ color: colors.textMuted, fontSize: '0.72rem', mt: 0.15 }}>
              {s.derivedFrom
                ? `Estimated from your ${s.derivedFrom} · test to measure`
                : 'Not tested yet · absolute default'}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontWeight: 900,
                fontSize: '1.35rem',
                color: colors.textMuted,
                lineHeight: 1,
              }}
            >
              {s.bestCda.toFixed(3)}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.7rem',
                color: colors.textFaint,
              }}
            >
              seeded
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

function PositionCard({ p, rank }: { p: PositionStat; rank: number }) {
  const c = positionColor(p.class);
  const displayName = p.alias || p.class;
  return (
    <Box
      component={RouterLink}
      to={`/positions/${encodeURIComponent(p.class)}`}
      sx={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        borderRadius: 3,
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.surfaceElevated,
        overflow: 'hidden',
        transition: 'border-color 120ms, transform 120ms',
        '&:hover': { borderColor: c, transform: 'translateY(-1px)' },
      }}
    >
      {/* Pose art hero */}
      <Box
        sx={{
          position: 'relative',
          backgroundColor: 'rgba(15,15,20,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 1,
        }}
      >
        <PoseGlyph positionClass={p.class} size={200} />
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.7rem',
              color: colors.textFaint,
              fontWeight: 700,
            }}
          >
            #{rank}
          </Typography>
        </Box>
        <Sparkline
          values={(p.recentBests ?? []).map((r) => r.bestCda)}
          color={c}
          width={140}
          height={26}
          style={{ position: 'absolute', bottom: 6, right: 10 }}
        />
      </Box>

      {/* Bottom summary strip */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                color: c,
                fontWeight: 800,
                fontSize: '0.9rem',
                letterSpacing: '0.02em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayName.toUpperCase()}
            </Typography>
            <Typography sx={{ color: colors.textMuted, fontSize: '0.72rem', mt: 0.15 }}>
              {p.sessionCount} session{p.sessionCount === 1 ? '' : 's'} · {formatHoldMinutes(p.totalHoldSec)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontWeight: 900,
                fontSize: '1.35rem',
                color: colors.brand,
                lineHeight: 1,
              }}
            >
              {p.bestCda.toFixed(3)}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.7rem',
                color: colors.textMuted,
              }}
            >
              avg {p.meanCda.toFixed(3)}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

function Sparkline({
  values,
  color,
  width,
  height,
  style,
}: {
  values: number[];
  color: string;
  width: number;
  height: number;
  style?: React.CSSProperties;
}) {
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(1e-6, max - min);
  const pts = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * (width - 4) + 2;
      // Invert: lower CdA = higher on chart.
      const y = height - 2 - ((max - v) / range) * (height - 4);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={width} height={height} style={style}>
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.85}
      />
    </svg>
  );
}
